import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const FAQ = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0); // First question is open by default
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleButtonContactUs = () => {
    navigate('/contact-us');
  };

  const faqs = [
    {
      question: t('faq.question1'),
      answer: t('faq.answer1')
    },
    {
      question: t('faq.question2'),
      answer: t('faq.answer2')
    },
    {
      question: t('faq.question3'),
      answer: t('faq.answer3')
    },
    {
      question: t('faq.question4'),
      answer: t('faq.answer4')
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const bannerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut", delay: 0.4 },
    },
  };

  return (
    <section ref={ref} className="bg-white border-t border-[#14C5D1] py-16 md:py-24">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Left Column - Heading and Description */}
          <motion.div
            className='max-w-[479px]'
            variants={slideInLeft}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="bg-gray-100 px-3 py-1 rounded-lg w-fit mb-6">
              <span className="text-sm text-[#5A5A59] font-medium">{t('faq.badge')}</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#000087] to-[#000000] bg-clip-text text-transparent mb-6 leading-tight">
              {t('faq.heading')}
            </h2>
            
            <p className="text-sm sm:text-base text-[#000929] leading-relaxed">
              {t('faq.description')}
            </p>
          </motion.div>

          {/* Right Column - FAQ Accordion */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="space-y-0">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className={`border-b border-gray-200 ${index === faqs.length - 1 ? 'border-b-0' : ''}`}
                  variants={itemVariants}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full py-4 sm:py-6 flex justify-between items-center text-left hover:opacity-80 transition-opacity"
                  >
                    <span className="text-base sm:text-lg font-semibold text-[#111827] pr-4 sm:pr-8">
                      {faq.question}
                    </span>
                    <span className="flex-shrink-0 text-2xl font-light text-[#14C5D1] w-6 h-6 flex items-center justify-center">
                      {openIndex === index ? (
                      <img src="/assets/minus.svg" alt="minus" />
                      ) : (
                        <span className="relative">
                        <img src="/assets/plus.svg" alt="plus" />
                        </span>
                      )}
                    </span>
                  </button>
                  
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="pb-4">
                          <p className="text-sm sm:text-base text-[#4B5563] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Call to Action Banner */}
        <motion.div
          className="bg-[#F8FBFC] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
          variants={bannerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="flex-1">
            <h3 className="text-lg sm:text-xl font-bold text-[#111827] mb-2">
              {t('faq.ctaBannerTitle')}
            </h3>
            <p className="text-sm sm:text-base text-[#4B5563]">
              {t('faq.ctaBannerDescription')}
            </p>
          </div>
          
          <button className="btn-primary" onClick={handleButtonContactUs}>
            {t('faq.ctaBannerButton')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;

